import { Semaphore } from 'async-mutex';

import type { LocaleData } from './global_locale';
import { isLocaleLoaded, setLocale } from './global_locale';

const localeLoadingSemaphore = new Semaphore(1);

const upstreamLocaleFiles = import.meta.glob<{
  default: LocaleData['messages'];
}>(['@/mastodon/locales/*.json']);
const localeFiles = import.meta.glob<{ default: LocaleData['messages'] }>([
  './*.json',
]);

// Resolve a locale glob entry, trying the exact tag first, then the base
// language (e.g. `fr-FR` → `fr`), then English. This lets standalone builds
// honour browser languages like `fr-FR` or `pt-PT` that lack an exact file,
// and is a no-op for regular builds where the backend sets a valid `lang`.
function resolveLocaleFile<T>(
  files: Record<string, () => Promise<T>>,
  prefix: string,
  locale: string,
): (() => Promise<T>) | undefined {
  const exact = `${prefix}${locale}.json`;
  if (Object.hasOwn(files, exact)) return files[exact];

  const base = locale.split('-')[0];
  if (base && base !== locale) {
    const baseFile = `${prefix}${base}.json`;
    if (Object.hasOwn(files, baseFile)) return files[baseFile];
  }

  return files[`${prefix}en.json`];
}

export async function loadLocale() {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- we want to match empty strings
  const locale = document.querySelector<HTMLElement>('html')?.lang || 'en';

  // We use a Semaphore here so only one thing can try to load the locales at
  // the same time. If one tries to do it while its in progress, it will wait
  // for the initial load to finish before it is resumed (and will see that locale
  // data is already loaded)
  await localeLoadingSemaphore.runExclusive(async () => {
    // if the locale is already set, then do nothing
    if (isLocaleLoaded()) return;

    // If there is no locale file, then fallback to english
    const upstreamLocaleFile = resolveLocaleFile(
      upstreamLocaleFiles,
      '/mastodon/locales/',
      locale,
    );

    if (!upstreamLocaleFile)
      throw new Error('Could not load the upstream locale JSON file');

    const { default: upstreamLocaleData } = await upstreamLocaleFile();

    // If there is no locale file, then fallback to english
    const localeFile = resolveLocaleFile(localeFiles, './', locale);

    if (!localeFile) throw new Error('Could not load the locale JSON file');

    const { default: localeData } = await localeFile();

    setLocale({ messages: { ...upstreamLocaleData, ...localeData }, locale });
  });
}
